import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

/* ─── Types ─── */

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration: number;
}

export interface ToastOptions {
  variant?: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toast: (options: ToastOptions) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

/* ─── Variant Config ─── */

const variantConfig: Record<
  ToastVariant,
  { icon: React.ElementType; borderColor: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle,
    borderColor: 'border-l-[var(--volt-success)]',
    iconColor: 'text-[var(--volt-success)]',
  },
  error: {
    icon: AlertCircle,
    borderColor: 'border-l-[var(--volt-error)]',
    iconColor: 'text-[var(--volt-error)]',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: 'border-l-[var(--volt-warning)]',
    iconColor: 'text-[var(--volt-warning)]',
  },
  info: {
    icon: Info,
    borderColor: 'border-l-[var(--volt-blue)]',
    iconColor: 'text-[var(--volt-blue)]',
  },
};

/* ─── Context ─── */

const ToastContext = createContext<ToastContextType | null>(null);

const MAX_TOASTS = 5;

/* ─── Provider ─── */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const duration = options.duration ?? 5000;
      const newToast: ToastItem = {
        id,
        variant: options.variant ?? 'info',
        title: options.title,
        description: options.description,
        duration,
      };

      setToasts((prev) => {
        const next = [...prev, newToast];
        // Keep only the last MAX_TOASTS
        return next.slice(-MAX_TOASTS);
      });

      // Auto-dismiss
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast],
  );

  const toast = useCallback(
    (options: ToastOptions) => addToast(options),
    [addToast],
  );

  const success = useCallback(
    (title: string, description?: string) =>
      addToast({ variant: 'success', title, description }),
    [addToast],
  );

  const error = useCallback(
    (title: string, description?: string) =>
      addToast({ variant: 'error', title, description, duration: 8000 }),
    [addToast],
  );

  const warning = useCallback(
    (title: string, description?: string) =>
      addToast({ variant: 'warning', title, description }),
    [addToast],
  );

  const info = useCallback(
    (title: string, description?: string) =>
      addToast({ variant: 'info', title, description }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info }}>
      {children}
      {createPortal(
        <ToastContainer toasts={toasts} onRemove={removeToast} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/* ─── Hook ─── */

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/* ─── Container ─── */

function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none">
      {toasts.map((t) => (
        <ToastCard key={t.id} item={t} onRemove={onRemove} />
      ))}
    </div>
  );
}

/* ─── Individual Toast ─── */

function ToastCard({ item, onRemove }: { item: ToastItem; onRemove: (id: string) => void }) {
  const config = variantConfig[item.variant];
  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={clsx(
        'pointer-events-auto flex items-start gap-3 p-4 rounded-[var(--volt-radius-lg)]',
        'bg-[var(--volt-dark-elevated)] border border-[var(--volt-dark-border)]',
        'border-l-4',
        config.borderColor,
        'shadow-[var(--volt-shadow-lg)]',
        'volt-animate-slide-in-right',
      )}
    >
      <Icon className={clsx('h-5 w-5 flex-shrink-0 mt-0.5', config.iconColor)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--volt-text-primary)]">{item.title}</p>
        {item.description && (
          <p className="mt-0.5 text-sm text-[var(--volt-text-muted)]">{item.description}</p>
        )}
      </div>
      <button
        onClick={() => onRemove(item.id)}
        className={clsx(
          'p-1 rounded-[var(--volt-radius-sm)]',
          'text-[var(--volt-text-muted)] hover:text-[var(--volt-text-primary)]',
          'hover:bg-[var(--volt-dark-surface)]',
          'transition-colors duration-[var(--volt-transition-fast)]',
        )}
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
